import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  LayoutDashboard,
  FileText,
  Search,
  Target,
  Bookmark,
  Globe,
  Briefcase,
  Settings,
  LogOut,
  Languages,
  X,
  Menu,
  MessageSquare,
  Mic,
  PenTool,
  LineChart,
  Linkedin,
  Banknote,
  Lightbulb,
  Building,
  Sparkles
} from 'lucide-react';
import toastUtil from '../../utils/toast';

export default function MobileSidebar({ isOpen, setIsOpen }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, toggleLanguage, isRTL } = useTheme();

  const sidebarTranslations = {
    en: {
      overview: 'Overview',
      cvBuilder: 'CV Builder',
      jobSearch: 'Free Job Search',
      jobMatch: 'Job Match AI',
      savedJobs: 'Job Library',
      remoteJobs: 'Remote Jobs',
      advancedSearch: 'Advanced Search',
      coverLetters: 'Cover Letters',
      chatbot: 'AI Agent',
      interviewPrep: 'Interview Simulator',
      careerGrowth: 'Career Growth',
      coverLetterGen: 'Cover Letter Generator',
      cvTranslator: 'CV Translator',
      atsMatcher: 'ATS Keyword Matcher',
      skillGap: 'Skill Gap Analysis',
      linkedinOpt: 'LinkedIn Optimizer',
      salaryPredictor: 'Salary Predictor',
      projectGen: 'Project Generator',
      culturalFit: 'Cultural Fit Analyzer',
      settings: 'Settings',
      logout: 'Logout',
      language: 'Language',
      menu: 'Menu'
    },
    ar: {
      overview: 'نظرة عامة',
      cvBuilder: 'منشئ السيرة الذاتية',
      jobSearch: 'بحث مجاني عن وظائف',
      jobMatch: 'مطابقة الوظائف',
      savedJobs: 'مكتبة الوظائف',
      remoteJobs: 'وظائف عن بعد',
      advancedSearch: 'بحث متقدم',
      coverLetters: 'رسائل التغطية',
      chatbot: 'وكيل الذكاء الاصطناعي',
      interviewPrep: 'محاكي المقابلات',
      careerGrowth: 'التطوير المهني',
      coverLetterGen: 'صانع رسائل التغطية',
      cvTranslator: 'مترجم السيرة الذاتية',
      atsMatcher: 'مطابق الـ ATS',
      skillGap: 'فجوة المهارات',
      linkedinOpt: 'مُحسن لينكد إن',
      salaryPredictor: 'متوقع الرواتب',
      projectGen: 'صانع المشاريع العملية',
      culturalFit: 'محلل ثقافة الشركات',
      settings: 'الإعدادات',
      logout: 'تسجيل الخروج',
      language: 'اللغة',
      menu: 'القائمة'
    },
    tr: {
      overview: 'Genel Bakış',
      cvBuilder: 'CV Oluşturucu',
      jobSearch: 'İş Arama',
      freeJobSearch: 'Ücretsiz İş Arama',
      jobMatch: 'İş Eşleştirme Yapay Zekası',
      savedJobs: 'İş Kütüphanesi',
      remoteJobs: 'Uzaktan İşler',
      advancedSearch: 'Gelişmiş Arama',
      coverLetters: 'Ön Yazılar',
      chatbot: 'Yapay Zeka Ajanı',
      interviewPrep: 'Mülakat Simülatörü',
      careerGrowth: 'Kariyer Gelişimi',
      coverLetterGen: 'Ön Yazı Oluşturucu',
      cvTranslator: 'CV Çevirmen',
      atsMatcher: 'ATS Anahtar Kelime Eşleştirici',
      skillGap: 'Beceri Boşluğu Analizi',
      linkedinOpt: 'LinkedIn Optimize Edici',
      salaryPredictor: 'Maaş Tahmin Edici',
      projectGen: 'Proje Oluşturucu',
      culturalFit: 'Kültürel Uyum Analizörü',
      settings: 'Ayarlar',
      logout: 'Çıkış Yap',
      language: 'Dil',
      menu: 'Menü'
    }
  };

  const tr = sidebarTranslations[language] || sidebarTranslations['en'];

  const coreItems = [
    { id: 'overview', icon: LayoutDashboard, label: tr.overview, path: '/dashboard' },
    { id: 'cv-builder', icon: FileText, label: tr.cvBuilder, path: '/cv-builder' },
    { id: 'job-search', icon: Search, label: tr.jobSearch, path: '/job-search' },
    { id: 'saved-jobs', icon: Bookmark, label: tr.savedJobs, path: '/job-library' },
    { id: 'remote-jobs', icon: Globe, label: tr.remoteJobs, path: '/remote-jobs' },
    { id: 'advanced-search', icon: Briefcase, label: tr.advancedSearch, path: '/advanced-search' },
    { id: 'cover-letters', icon: FileText, label: tr.coverLetters, path: '/cover-letters' },
  ];

  const aiItems = [
    { id: 'job-match', icon: Target, label: tr.jobMatch, path: '/job-match' },
    { id: 'chatbot', icon: MessageSquare, label: tr.chatbot, path: '/chatbot' },
    { id: 'interview-prep', icon: Mic, label: tr.interviewPrep, path: '/interview-prep' },
    { id: 'cover-letter-gen', icon: PenTool, label: tr.coverLetterGen, path: '/cover-letter-generator' },
    { id: 'cv-translator', icon: Languages, label: tr.cvTranslator, path: '/cv-translator' },
    { id: 'ats-matcher', icon: Search, label: tr.atsMatcher, path: '/ats-keyword-matcher' },
    { id: 'skill-gap', icon: LineChart, label: tr.skillGap, path: '/skill-gap-analysis' },
    { id: 'linkedin-opt', icon: Linkedin, label: tr.linkedinOpt, path: '/linkedin-optimizer' },
    { id: 'salary-predictor', icon: Banknote, label: tr.salaryPredictor, path: '/salary-predictor' },
    { id: 'project-gen', icon: Lightbulb, label: tr.projectGen, path: '/project-generator' },
    { id: 'cultural-fit', icon: Building, label: tr.culturalFit, path: '/cultural-fit' },
  ];

  const isActivePath = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  async function handleLogout() {
    try {
      await logout();
      toastUtil.success('Logged out successfully');
      navigate('/');
      setIsOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
      toastUtil.error('Failed to logout');
    }
  }

  function handleToggleLanguage() {
    const nextLang = language === 'en' ? 'ar' : 'en';
    toggleLanguage();
    toastUtil.success(nextLang === 'ar' ? 'تم التبديل إلى العربية' : 'Switched to English');
  }

  const handleNavClick = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  return (
    <>
      {/* Drawer Overlay */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/50 z-[60] md:hidden"
            />

            {/* Bottom Sheet */}
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed bottom-0 left-0 right-0 h-[85vh] bg-[#F8FAF8]/95 backdrop-blur-2xl border-t border-white shadow-[0_-10px_40px_rgba(0,0,0,0.15)] z-[60] flex flex-col md:hidden rounded-t-[32px]"
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              {/* Drag Handle Indicator */}
              <div className="w-full flex justify-center pt-3 pb-1">
                <div className="w-12 h-1.5 bg-gray-300 rounded-full"></div>
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-6 pb-4 border-b border-[#E5E7EB]">
                <Link to="/dashboard" className="flex items-center" dir="ltr" onClick={() => setIsOpen(false)}>
                  <img 
                    src="/cvlogs-logo.png" 
                    alt="CVLogs" 
                    className="w-8 h-8 object-contain"
                  />
                  <span className="text-lg font-bold text-[#333333] mt-1 ml-2">Logs</span>
                </Link>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleToggleLanguage}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#2F80ED]/10 text-[#2F80ED] hover:bg-[#2F80ED]/20 transition-all font-bold text-sm"
                  >
                    <Languages className="w-4 h-4" />
                    <span>{language === 'ar' ? 'AR' : language === 'tr' ? 'TR' : 'EN'}</span>
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                  >
                    <X className="w-5 h-5 text-[#333333]" />
                  </button>
                </div>
              </div>

              {/* Navigation */}
              <nav className="flex-1 p-4 overflow-y-auto professional-scrollbar">
                {/* Core Items */}
                <div className="mb-6 space-y-1">
                  <div className="px-4 py-2 text-xs font-semibold text-[#6B7280] uppercase tracking-wider">
                    {language === 'ar' ? 'الخدمات الأساسية' : 'Core Features'}
                  </div>
                  {coreItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = isActivePath(item.path);
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleNavClick(item.path)}
                        className={`
                          w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all
                          ${isActive
                            ? 'bg-[#2F80ED] text-white shadow-lg'
                            : 'text-[#333333] hover:bg-[#2F80ED]/5 hover:text-[#2F80ED]'
                          }
                        `}
                      >
                        <Icon 
                          className={`
                            w-5 h-5 flex-shrink-0
                            ${isActive ? 'text-white' : 'text-[#6B7280]'}
                          `} 
                        />
                        <span className="font-medium text-sm sm:text-base truncate">{item.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* AI Items */}
                <div className="mb-6 space-y-1">
                  <div className="px-4 py-2 text-xs font-semibold flex items-center gap-2 text-[#2F80ED] uppercase tracking-wider">
                    <Sparkles className="w-3 h-3" />
                    {language === 'ar' ? 'أدوات الذكاء الاصطناعي' : 'AI Features'}
                  </div>
                  {aiItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = isActivePath(item.path);
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleNavClick(item.path)}
                        className={`
                          w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all
                          ${isActive
                            ? 'bg-[#2F80ED] text-white shadow-lg'
                            : 'text-[#333333] hover:bg-[#2F80ED]/5 hover:text-[#2F80ED]'
                          }
                        `}
                      >
                        <Icon 
                          className={`
                            w-5 h-5 flex-shrink-0
                            ${isActive ? 'text-white' : 'text-[#6B7280]'}
                          `} 
                        />
                        <span className="font-medium text-sm sm:text-base truncate">{item.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Bottom Actions inside scroll area so they don't hover permanently */}
                <div className="pt-2 pb-[100px] mt-4 border-t border-[#E5E7EB]/50 space-y-1">
                
                <button
                  onClick={() => handleNavClick('/settings')}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all
                    ${isActivePath('/settings')
                      ? 'bg-[#2F80ED]/10 text-[#2F80ED] font-bold'
                      : 'text-[#333333] hover:bg-white/50 hover:text-[#2F80ED]'
                    }
                  `}
                >
                  <Settings className={`w-5 h-5 ${isActivePath('/settings') ? 'text-white' : 'text-[#6B7280]'}`} />
                  <span className="font-medium text-sm sm:text-base">{tr.settings}</span>
                </button>
                
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[#EB5757] hover:bg-red-50/50 transition-all"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium text-sm sm:text-base">{tr.logout}</span>
                </button>
                </div>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
